/**
 * サイドバー (メニュー)
 */

import {
  Box,
  BoxProps,
  CloseButton,
  Flex,
  FlexProps,
  Icon,
  Image,
  SystemStyleObject,
  useBreakpointValue,
  useColorModeValue,
  useConst,
} from '@chakra-ui/react';
import React from 'react';
import { IconType } from 'react-icons';
import {
  FiArrowUpCircle,
  FiAtSign,
  FiCalendar,
  FiCreditCard,
  FiDollarSign,
  FiFileText,
  FiHelpCircle,
  FiHome,
  FiShoppingBag,
  FiStar,
} from 'react-icons/fi';
import { Link, Navigate } from 'react-router-dom';

import { TypeUtils } from '../../../framework/utils/UtilityTypes';
import { pagePaths } from '../../constants/Page';
import { useGlobalStateMachine } from '../../GlobalStore';
import {
  Display,
  FlexWrap,
  MarginLeft,
  MarginX,
  Overflow,
  Width,
} from '../../models/properties';
import { useMasterContext } from '../MasterProvider';

type LinkItemProps = {
  name: string;
  icon: IconType;
  path: string;
  subPath?: LinkItemProps[];
  disabled?: boolean;
  hidden?: boolean;
};
type NavItemProps = FlexProps & {
  icon: IconType;
  path: string;
  disabled?: boolean;
  onClose: Runnable;
  pathname: string;
};
type SidebarProps = BoxProps & { onClose: Runnable; pathname: string };

/**
 * リンク
 *
 * @param icon - アイコン
 * @param path - リンク先
 * @param children - 子コンポーネント
 * @returns
 */
const NavItem: React.FunctionComponent<NavItemProps> = ({
  icon,
  path,
  children,
  disabled,
  hidden,
  onClose,
  pathname,
  ...props
}) => {
  const linkStyle = useConst<React.CSSProperties>({
    pointerEvents: !!disabled ? 'none' : 'unset',
    textDecoration: 'none',
  });
  const hover = useConst<SystemStyleObject>({
    bg: 'cyan.400',
    color: 'white',
  });
  const linkIconGroupHover = useConst<SystemStyleObject>({ color: 'white' });
  const marginX = useBreakpointValue<MarginX>({ base: 4, md: 3 });

  return (
    <Link
      hidden={hidden}
      onClick={() => (pathname === path ? onClose() : null)}
      style={linkStyle}
      to={path}
    >
      <Flex
        align="center"
        borderRadius="lg"
        cursor="pointer"
        color={disabled ? 'blackAlpha.500' : undefined}
        marginX={marginX}
        padding="3"
        role="group"
        _hover={hover}
        {...props}
      >
        {icon && (
          <Icon
            as={icon}
            fontSize="16"
            marginRight="4"
            _groupHover={linkIconGroupHover}
          />
        )}
        {children}
      </Flex>
    </Link>
  );
};

/**
 * サイドバーメニュー
 *
 * @param onClose - サイドバーを閉じるメソッド
 * @returns
 */
const SidebarContent: React.FunctionComponent<SidebarProps> = ({
  onClose,
  pathname,
  ...props
}) => {
  const { state } = useGlobalStateMachine();
  if (TypeUtils.isNil(state.userInfo)) {
    return <Navigate to={pagePaths.ROOT} />;
  }
  const master = useMasterContext();

  const isOutsourcerRepresent = React.useMemo(
    () =>
      !TypeUtils.isNil(master.role.representative) &&
      state.userInfo?.role_id.includes(master.role.representative!.id),
    [state.userInfo?.role_id]
  );

  /**
   * リンクに表示するアイテム
   */
  const LinkItems: Array<LinkItemProps> = React.useMemo(
    () => [
      { name: 'Top', icon: FiHome, path: pagePaths.TOP },
      { name: '日次業務', icon: FiCalendar, path: pagePaths.DAILY_OPERATION },
      {
        name: '月次申請',
        icon: FiArrowUpCircle,
        path: pagePaths.MONTHLY_OPERATION,
        subPath: [
          {
            name: 'シフト申請',
            icon: FiCalendar,
            path: pagePaths.SHIFT_APPLICATION,
            hidden:
              !TypeUtils.isNil(master.role.representative) &&
              state.userInfo!.role_id.includes(master.role.representative?.id),
          },
          {
            name: '勤怠申請',
            icon: FiFileText,
            path: pagePaths.ATTENDANCE_REPORT,
            hidden: state.userInfo?.roleOptions?.isOutsourcer,
          },
          {
            name: '交通費・経費申請',
            icon: FiShoppingBag,
            path: pagePaths.EXPENSE_REIMBURSEMENT,
          },
          {
            name: '給与確認',
            icon: FiDollarSign,
            path: pagePaths.PAYSLIP,
            hidden: state.userInfo?.roleOptions?.isOutsourcer,
          },
          {
            name: '請求',
            icon: FiCreditCard,
            path: pagePaths.INVOICE,
            hidden: !state.userInfo?.roleOptions?.isOutsourcer,
          },
        ],
      },
      {
        name: 'メンバ管理',
        icon: FiAtSign,
        path: pagePaths.WORKER_MANAGEMENT,
        hidden: !isOutsourcerRepresent,
      },
      {
        name: 'お知らせ検索',
        icon: FiStar,
        path: pagePaths.NOTIFICATION,
      },
      { name: 'ヘルプ', icon: FiHelpCircle, path: '', disabled: true },
    ],
    [state.userInfo, isOutsourcerRepresent, master.role.representative]
  );

  const boxWidth = useBreakpointValue<Width>({ base: 'full', md: 60 });
  const closeButtonDisplay = useBreakpointValue<Display>({
    base: 'flex',
    md: 'none',
  });
  const linksFlexWrap = useBreakpointValue<FlexWrap>({
    base: 'wrap',
    md: 'nowrap',
  });
  const linksOverflow = useBreakpointValue<Overflow>({
    base: 'hidden',
    md: 'auto',
  });
  const childMarginLeft = useBreakpointValue<MarginLeft>({
    base: 10,
    md: 9,
  });

  return (
    <Box
      background={useColorModeValue('white', 'gray.900')}
      borderRight="1px"
      borderRightColor={useColorModeValue('gray.200', 'gray.700')}
      height="full"
      position="fixed"
      transition="3s ease"
      width={boxWidth}
      {...props}
    >
      <Flex
        alignItems="center"
        height={20}
        justifyContent="space-between"
        marginX="8"
      >
        <Image src={`${process.env.REACT_APP_APP_ROOT}/logo.png`}></Image>
        <CloseButton display={closeButtonDisplay} onClick={onClose} />
      </Flex>
      <Box
        display="flex"
        flexDirection="column"
        flexWrap={linksFlexWrap}
        height="calc(100% - 5rem)"
        overflow={linksOverflow}
        width="100%"
      >
        {LinkItems.map(({ name, subPath, ...link }, idx1) =>
          TypeUtils.isNil(subPath) ? (
            <Box key={`main-${idx1}`}>
              <NavItem
                fontSize="xl"
                onClose={onClose}
                pathname={pathname}
                {...link}
                // maxW="48vw"
              >
                {name}
              </NavItem>
            </Box>
          ) : (
            <Box key={`main-${idx1}`}>
              <NavItem
                fontSize="xl"
                onClose={onClose}
                pathname={pathname}
                {...link}
                // maxW="48vw"
              >
                {name}
              </NavItem>
              {subPath.map(({ name: subName, ...subLink }, idx2) => (
                <NavItem
                  fontSize="lg"
                  key={`sub-${idx2}`}
                  onClose={onClose}
                  marginLeft={childMarginLeft}
                  pathname={pathname}
                  padding={1}
                  // maxW="48vw"
                  {...subLink}
                >
                  {subName}
                </NavItem>
              ))}
            </Box>
          )
        )}
      </Box>
    </Box>
  );
};

export default SidebarContent;
